import React from "react";
import { Link } from "react-router-dom";
import { Icon } from "@/components/Icon";
import TeamLogo from "./TeamLogo";
import { OddsButton } from "./sb";
import { leagueLabel } from "./SBBoardGame";
import { teamThemeColor } from "@/constants/teamThemes";
import { hasStartedPregame } from "@/lib/sportsbookPriority";

/* DiscoveryGameCard (design SBXGameCard.dc.html) — a self-contained game card used as
   the mobile board marquee and in the Home "Sportsbook" shelf. Team-colour washes, a
   status line, two team rows, a moneyline stack, and a "Moneyline · N markets" footer.
   The whole card links to the event; tapping a price adds to the slip without navigating. */

const hexA = (c, a) => {
  const h = String(c || "#3F3F46").replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map((x) => x + x).join("") : h, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};
function fmtStart(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" }); } catch { return ""; }
}
function mlFor(ev, side) { return (ev.outcomes || []).find((o) => o.market === "moneyline" && o.side === side); }

export default function DiscoveryGameCard({ event, onPick, isPicked, mobile = false }) {
  if (!event) return null;
  const isLive = event.status === "live";
  const liveScore = isLive && event.gs_home_score != null;
  const sa = liveScore ? Number(event.gs_away_score) : null;
  const sh = liveScore ? Number(event.gs_home_score) : null;
  const aML = mlFor(event, "away"), hML = mlFor(event, "home");
  const started = hasStartedPregame(event); // kicked off but not live -> lock the pre-game price
  const wa = hexA(teamThemeColor({ team: event.away_team, league: event.league, sport: event.sport }), 0.16);
  const wh = hexA(teamThemeColor({ team: event.home_team, league: event.league, sport: event.sport }), 0.16);
  const marketCount = event.market_count || 0;
  const pad = mobile ? "14px" : "16px 18px";
  const g1 = mobile ? 12 : 14;
  const th = mobile ? 32 : 36, cs = mobile ? 30 : 34;
  const ns = mobile ? 15 : 16, ss = mobile ? 22 : 24;

  const addPick = (o) => (e) => { e?.preventDefault?.(); e?.stopPropagation?.(); onPick(event, o); };
  const teamRow = (name, score, other) => (
    <div className="flex items-center gap-3" style={{ height: th, minWidth: 0 }}>
      <TeamLogo name={name} league={event.league} sport={event.sport} size={cs} />
      <span className="min-w-0 flex-1 truncate font-display font-bold text-white" style={{ fontSize: ns }}>{name}</span>
      {score != null && <span className="font-display font-extrabold tabular-nums" style={{ fontSize: ss, lineHeight: 1, letterSpacing: "-.03em", color: score >= other ? "#fff" : "#8A8A93" }}>{score}</span>}
    </div>
  );

  return (
    <Link to={`/event/${event.id}`} data-testid={`discovery-card-${event.id}`}
      className="relative flex h-full flex-col overflow-hidden rounded-[18px]"
      style={{ background: "linear-gradient(145deg, hsl(var(--ink-700)) 0%, hsl(var(--surface-3)) 45%, hsl(var(--ink-800)) 100%)", boxShadow: "inset 0 1px 0 hsl(var(--edge-light)), inset 0 -1px 0 hsl(var(--edge-dark)), var(--elev-2)", border: "1px solid hsl(var(--surface-border))" }}>
      <div className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(70% 90% at 0% 0%, ${wa} 0%, transparent 70%), radial-gradient(70% 90% at 100% 0%, ${wh} 0%, transparent 70%)` }} />
      <div className="relative flex flex-col" style={{ gap: g1, padding: pad }}>
        <div className="flex items-center gap-2" style={{ height: 22 }}>
          <span className="uppercase" style={{ font: "800 10px Outfit", letterSpacing: ".18em", color: "#a1a1aa" }}>{leagueLabel(event.league) || event.sport}</span>
          {isLive ? (
            <>
              <span className="inline-flex items-center gap-1.5 rounded-md" style={{ height: 20, padding: "0 7px", background: "rgba(239,68,68,.12)", boxShadow: "inset 0 0 0 1px rgba(239,68,68,.32)", font: "800 10px Outfit", letterSpacing: ".14em", color: "#EF4444" }}>
                <span className="h-1.5 w-1.5 rounded-full bg-[#EF4444]" style={{ animation: "pnrPulse 1.6s ease-out infinite" }} />LIVE
              </span>
              {event.gs_period && <span className="whitespace-nowrap" style={{ font: "700 12px Manrope", color: "#E4E4E7" }}>{event.gs_period}</span>}
            </>
          ) : <span className="whitespace-nowrap" style={{ font: "600 12px Outfit", color: "#a1a1aa" }}>{fmtStart(event.start_time)}</span>}
          {marketCount > 0 && <span className="ml-auto inline-flex items-center rounded-[5px]" style={{ height: 18, padding: "0 5px", boxShadow: "inset 0 0 0 1px #2F2F35", font: "800 9px Outfit", letterSpacing: ".12em", color: "#a1a1aa" }}>SGP</span>}
        </div>
        <div className="flex flex-col gap-2">
          {teamRow(event.away_team, sa, sh)}
          {teamRow(event.home_team, sh, sa)}
        </div>
        <div className="grid grid-cols-2 gap-2">
          {aML ? <OddsButton variant="stack" line={event.away_team?.split(" ").slice(-1)[0]} price={aML.odds} selected={isPicked(event.id, aML.id)} locked={started || aML.suspended === true || aML.active === false} onClick={addPick(aML)} testid={`odds-disc-${event.id}-away`} /> : <OddsButton na variant="stack" />}
          {hML ? <OddsButton variant="stack" line={event.home_team?.split(" ").slice(-1)[0]} price={hML.odds} selected={isPicked(event.id, hML.id)} locked={started || hML.suspended === true || hML.active === false} onClick={addPick(hML)} testid={`odds-disc-${event.id}-home`} /> : <OddsButton na variant="stack" />}
        </div>
      </div>
      <div className="relative mt-auto flex items-center gap-1.5" style={{ height: 44, padding: `0 ${mobile ? 14 : 18}px`, borderTop: "1px solid #1A1A1E", background: "rgba(10,10,10,.28)" }}>
        <span style={{ font: "600 13px Outfit", color: "#D4D4D8" }}>{started ? "Started" : "Moneyline, spreads & props"}</span>
        <Icon name="chevron-right" size={13} className="ml-auto text-[#71717a]" />
      </div>
    </Link>
  );
}
